import {
    Box, Typography, Button, Modal, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, IconButton, Select, MenuItem,
    FormControl, InputLabel, Tooltip, Skeleton, Avatar, Autocomplete, CircularProgress
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import { Menu as PrimeMenu } from 'primereact/menu';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import SearchIcon from '@mui/icons-material/Search';
import CustomAxios from "../../utils/CustomAxios";
import { useGetInventory } from "../globalFunctions";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const STATUS_OPTIONS = [
    { label: 'Active', value: 'active' },
    { label: 'On Notice', value: 'on_notice' },
    { label: 'Relieved', value: 'relieved' },
    { label: 'Suspended', value: 'suspended' },
];

// Mirrors the chip treatment used by category_table.js so the module doesn't
// introduce a second visual language for the same idea.
const STATUS_CHIP = {
    active: { bg: 'button.backgroundEdit', border: 'borders.green', color: 'borders.green', label: 'Active' },
    on_notice: { bg: '#FFF7E6', border: '#FA8C16', color: '#FA8C16', label: 'On Notice' },
    relieved: { bg: 'button.backgroundDelete', border: 'borders.red', color: 'borders.red', label: 'Relieved' },
    suspended: { bg: 'button.backgroundDelete', border: 'borders.red', color: 'borders.red', label: 'Suspended' },
};

const EMPTY_FORM = {
    full_name: '',
    mobileno: '',
    email: '',
    hub_id: '',
    department_id: '',
    designation: '',
    reporting_manager_id: '',
    shift_id: '',
    user_id: '',
    date_of_joining: '',
    status: 'active',
};

function StatusChip({ status }) {
    const style = STATUS_CHIP[status] || STATUS_CHIP.active;
    return (
        <Box sx={{
            display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
            backgroundColor: style.bg, border: '1px solid', borderColor: style.border,
            borderRadius: '2px', padding: '1px 8px', minWidth: '80px'
        }}>
            <Typography sx={{
                fontSize: '12px', fontWeight: 400, lineHeight: '20px',
                color: style.color, textTransform: 'uppercase'
            }}>
                {style.label}
            </Typography>
        </Box>
    );
}

function StatCard({ label, value, hint, accent }) {
    return (
        <Box sx={{
            flex: 1, minWidth: '160px', backgroundColor: 'background.light',
            borderRadius: '12px', padding: '13px 16px',
            boxShadow: '0px 0px 25px 0px #00000012',
            borderLeft: '3px solid', borderColor: accent
        }}>
            <Typography sx={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.07em', color: 'text.secondary' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 600, lineHeight: 1.25 }}>{value}</Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>{hint}</Typography>
        </Box>
    );
}

function initialsOf(name) {
    if (!name) return '--';
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

export function StaffDirectory() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const inventory = useSelector(state => state.inventory.inventory);
    const getInventory = useGetInventory();
    const toast = useRef(null);
    const menuRefs = useRef([]);

    const [staffList, setStaffList] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [managers, setManagers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');

    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);

    // Account linking is a search-as-you-type against unlinked accounts only.
    const [userOptions, setUserOptions] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [userLoading, setUserLoading] = useState(false);
    const [linkedUserLabel, setLinkedUserLabel] = useState('');

    // Leave entitlements. Without a row for the year, every leave request that
    // staff member makes lands as loss of pay — so this form is not optional.
    const [balanceOpen, setBalanceOpen] = useState(false);
    const [balanceStaff, setBalanceStaff] = useState(null);
    const [balanceYear, setBalanceYear] = useState(new Date().getFullYear());
    const [balances, setBalances] = useState([]);
    const [balanceEdits, setBalanceEdits] = useState({});
    const [balanceLoading, setBalanceLoading] = useState(false);

    const [relieveOpen, setRelieveOpen] = useState(false);
    const [relieveStaff, setRelieveStaff] = useState(null);
    const [relieveForm, setRelieveForm] = useState({
        resignation_date: dayjs().format('YYYY-MM-DD'),
        notice_period_days: 30,
        last_working_day: dayjs().add(30, 'day').format('YYYY-MM-DD'),
        reason: '',
    });

    // The store holds either a number or a single-element array depending on
    // which screen last wrote it, so normalize before it reaches a query param.
    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const showToast = (severity, summary, detail) => {
        if (toast.current) toast.current.show({ severity, summary, detail, life: 3000 });
    };

    const errorMessage = (error, fallback) => {
        const detail = error?.response?.data?.error;
        if (!detail) return fallback;
        if (typeof detail === 'string') return detail;
        const firstKey = Object.keys(detail)[0];
        const firstValue = detail[firstKey];
        return Array.isArray(firstValue) ? firstValue[0] : String(firstValue);
    };

    useEffect(() => {
        getInventory();
    }, []);

    useEffect(() => {
        if (!hubId) return;
        fetchStaff();
        fetchStats();
        fetchDepartments();
        fetchShifts();
        fetchManagers();
    }, [hubId]);

    useEffect(() => {
        if (!hubId) return;
        const timer = setTimeout(fetchStaff, 300);
        return () => clearTimeout(timer);
    }, [search, departmentFilter, statusFilter]);

    // Debounced lookup for the "link login account" picker.
    useEffect(() => {
        if (userSearch.trim().length < 3) {
            setUserOptions([]);
            return;
        }
        setUserLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await CustomAxios.get('staff/linkable-users/', { params: { q: userSearch } });
                setUserOptions(res.data || []);
            } catch (error) {
                setUserOptions([]);
            } finally {
                setUserLoading(false);
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [userSearch]);

    const fetchStaff = async () => {
        try {
            setLoading(true);
            const params = { hub_id: hubId };
            if (search) params.q = search;
            if (departmentFilter) params.department_id = departmentFilter;
            if (statusFilter === 'relieved') {
                params.status = 'relieved';
                params.is_active = 'all';
            } else if (statusFilter) {
                params.status = statusFilter;
            }
            const res = await CustomAxios.get('staff/', { params });
            setStaffList(res.data || []);
            menuRefs.current = (res.data || []).map((_, i) => menuRefs.current[i] || React.createRef());
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to fetch staff'));
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await CustomAxios.get('staff/stats/', { params: { hub_id: hubId } });
            setStats(res.data);
        } catch (error) {
            setStats(null);
        }
    };

    const fetchDepartments = async () => {
        try {
            const res = await CustomAxios.get('staff/departments/', { params: { hub_id: hubId, is_active: true } });
            setDepartments(res.data || []);
        } catch (error) {
            setDepartments([]);
        }
    };

    const fetchShifts = async () => {
        try {
            const res = await CustomAxios.get('staff/shifts/', { params: { hub_id: hubId, is_active: true } });
            setShifts(res.data || []);
        } catch (error) {
            setShifts([]);
        }
    };

    const fetchManagers = async (excludeId) => {
        try {
            const params = { hub_id: hubId };
            if (excludeId) params.exclude_id = excludeId;
            const res = await CustomAxios.get('staff/options/', { params });
            setManagers(res.data || []);
        } catch (error) {
            setManagers([]);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditMode(true);
            setSelectedId(item.id);
            setFormData({
                full_name: item.full_name || '',
                mobileno: item.mobileno || '',
                email: item.email || '',
                hub_id: item.hub || hubId || '',
                department_id: item.department || '',
                designation: item.designation || '',
                reporting_manager_id: item.reporting_manager || '',
                shift_id: item.shift || '',
                user_id: item.user || '',
                date_of_joining: item.date_of_joining || '',
                status: item.status || 'active',
            });
            setLinkedUserLabel(item.user ? `${item.user_mobile || 'linked account'}` : '');
            fetchManagers(item.id);
        } else {
            setEditMode(false);
            setSelectedId(null);
            setFormData({ ...EMPTY_FORM, hub_id: hubId || '' });
            setLinkedUserLabel('');
            fetchManagers();
        }
        setUserSearch('');
        setUserOptions([]);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditMode(false);
        setSelectedId(null);
        setFormData(EMPTY_FORM);
        setLinkedUserLabel('');
    };

    const handleSubmit = async () => {
        if (!formData.full_name.trim()) {
            showToast('error', 'Validation Error', 'Full name is required');
            return;
        }
        if (!formData.hub_id) {
            showToast('error', 'Validation Error', 'Hub is required');
            return;
        }
        if (!formData.date_of_joining) {
            showToast('error', 'Validation Error', 'Date of joining is required');
            return;
        }

        // Send null rather than '' for the optional relations — DRF reads an
        // empty string as a bad integer, not as "not set".
        const payload = {
            ...formData,
            department_id: formData.department_id || null,
            reporting_manager_id: formData.reporting_manager_id || null,
            shift_id: formData.shift_id || null,
            user_id: formData.user_id || null,
            email: formData.email || null,
            designation: formData.designation || null,
        };

        try {
            setSaving(true);
            if (editMode) {
                await CustomAxios.put(`staff/${selectedId}/`, payload);
            } else {
                await CustomAxios.post('staff/create/', payload);
            }
            showToast('success', 'Success', editMode ? 'Staff updated successfully' : 'Staff created successfully');
            handleCloseModal();
            fetchStaff();
            fetchStats();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Operation failed'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async (item) => {
        if (!window.confirm(`Deactivate ${item.full_name}? Their attendance and leave history is kept.`)) return;
        try {
            setLoading(true);
            await CustomAxios.delete(`staff/${item.id}/`);
            showToast('success', 'Success', 'Staff deactivated successfully');
            fetchStaff();
            fetchStats();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Deactivate failed'));
        } finally {
            setLoading(false);
        }
    };

    const openBalances = async (item, year = balanceYear) => {
        setBalanceStaff(item);
        setBalanceYear(year);
        setBalanceOpen(true);
        setBalanceEdits({});
        try {
            setBalanceLoading(true);
            const res = await CustomAxios.get('staff/leave-balance/', {
                params: { staff_id: item.id, year },
            });
            setBalances(res.data.balances || []);
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load balances'));
            setBalances([]);
        } finally {
            setBalanceLoading(false);
        }
    };

    const saveBalances = async () => {
        const changed = Object.entries(balanceEdits);
        if (!changed.length) {
            setBalanceOpen(false);
            return;
        }
        try {
            setSaving(true);
            // One request per category — the endpoint sets a single entitlement,
            // and a partial failure should not be hidden behind a batch.
            for (const [category, values] of changed) {
                await CustomAxios.put('staff/leave-balance/update/', {
                    staff_id: balanceStaff.id,
                    year: balanceYear,
                    leave_category: category,
                    ...values,
                });
            }
            showToast('success', 'Success', 'Entitlements updated');
            setBalanceOpen(false);
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not save entitlements'));
        } finally {
            setSaving(false);
        }
    };

    const balanceValue = (row, field) => {
        const edit = balanceEdits[row.leave_category];
        if (edit && field in edit) return edit[field];
        return row[field];
    };

    const editBalance = (row, field, value) => {
        setBalanceEdits(prev => ({
            ...prev,
            [row.leave_category]: { ...prev[row.leave_category], [field]: value },
        }));
    };

    const openRelieve = (item) => {
        setRelieveStaff(item);
        setRelieveForm({
            resignation_date: dayjs().format('YYYY-MM-DD'),
            notice_period_days: 30,
            last_working_day: dayjs().add(30, 'day').format('YYYY-MM-DD'),
            reason: '',
        });
        setRelieveOpen(true);
    };

    const submitRelieve = async () => {
        if (!relieveForm.resignation_date || !relieveForm.last_working_day) {
            showToast('error', 'Validation Error', 'Resignation date and last working day are both required');
            return;
        }
        try {
            setSaving(true);
            await CustomAxios.post('staff/relieving/initiate/', {
                staff_id: relieveStaff.id,
                ...relieveForm,
            });
            showToast('success', 'Started', `${relieveStaff.full_name} is now on notice. Continue on the Relieving screen.`);
            setRelieveOpen(false);
            fetchStaff();
            fetchStats();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not start relieving'));
        } finally {
            setSaving(false);
        }
    };

    const hubName = useMemo(
        () => inventory?.find(inv => inv.value === hubId)?.name || '',
        [inventory, hubId]
    );

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 2, flexWrap: 'wrap', mb: 3
            }}>
                <Typography variant="h5">Staff Directory</Typography>
                <Box sx={{
                    display: 'flex', gap: { xs: '8px', md: '14px' }, alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' }
                }}>
                    <TextField
                        size="small"
                        placeholder="Search name / employee ID"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} /> }}
                        sx={{ minWidth: '230px' }}
                    />
                    <FormControl size="small" sx={{ minWidth: '160px' }}>
                        <InputLabel>Department</InputLabel>
                        <Select
                            value={departmentFilter}
                            label="Department"
                            onChange={(e) => setDepartmentFilter(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            {departments.map(dep => (
                                <MenuItem key={dep.id} value={dep.id}>{dep.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: '140px' }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            {STATUS_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
                        Add Staff
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', mb: 3 }}>
                <StatCard label="Total staff" value={stats?.total ?? '—'} hint={hubName || 'selected hub'} accent="primary.main" />
                <StatCard label="Active" value={stats?.active ?? '—'} hint="on roll today" accent="borders.green" />
                <StatCard label="On notice" value={stats?.on_notice ?? '—'} hint="relieving in progress" accent="#FA8C16" />
                <StatCard label="Joined this month" value={stats?.joined_this_month ?? '—'} hint={dayjs().format('MMM YYYY')} accent="button.blue" />
                <StatCard label="Relieved" value={stats?.relieved ?? '—'} hint="archived, read-only" accent="secondary.border" />
            </Box>

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Employee ID', 'Staff', 'Department', 'Designation', 'Reporting manager',
                              'Shift', 'Joined', 'Login', 'Status', ''].map((head, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', whiteSpace: 'nowrap' }}>
                                    {head}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={10} sx={{ backgroundColor: 'white', height: '200px' }}>
                                    {[0, 1, 2].map(i => (
                                        <Skeleton key={i} animation="wave" sx={{ height: '46px', background: 'rgb(0 0 0 / 6%)' }} />
                                    ))}
                                </TableCell>
                            </TableRow>
                        ) : staffList.length ? staffList.map((item, ind) => (
                            <TableRow key={item.id}>
                                <TableCell sx={{ backgroundColor: 'white', fontFamily: 'monospace', fontSize: '12px', color: 'text.secondary' }}>
                                    {item.employee_id}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Avatar sx={{ width: 30, height: 30, fontSize: '11px', bgcolor: 'background.table', color: 'button.blue' }}>
                                            {initialsOf(item.full_name)}
                                        </Avatar>
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.full_name}</Typography>
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{item.mobileno || '—'}</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{item.department_name || '—'}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{item.designation || '—'}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{item.reporting_manager_name || '—'}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white', whiteSpace: 'nowrap' }}>
                                    {item.shift_name ? `${item.shift_name} · ${item.shift_timing}` : '—'}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white', whiteSpace: 'nowrap' }}>
                                    {item.date_of_joining ? dayjs(item.date_of_joining).format('DD MMM YYYY') : '—'}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <Typography sx={{ fontSize: '11px', color: item.has_login ? 'button.blue' : 'text.secondary' }}>
                                        {item.has_login ? 'Linked' : 'No account'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <StatusChip status={item.status} />
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <PrimeMenu
                                        model={[
                                            { label: 'Edit details', command: () => handleOpenModal(item) },
                                            { label: 'Leave balance', command: () => openBalances(item) },
                                            ...(item.status === 'active'
                                                ? [{ label: 'Mark for relieving', command: () => openRelieve(item) }]
                                                : []),
                                            { label: 'Deactivate', command: () => handleDeactivate(item) },
                                        ]}
                                        popup
                                        ref={menuRefs.current[ind]}
                                        style={{ marginTop: '-10px' }}
                                        className="custom-menu"
                                    />
                                    <Tooltip title="Actions" arrow>
                                        <IconButton
                                            sx={{ textTransform: 'none' }}
                                            onClick={(event) => menuRefs.current[ind]?.current?.toggle(event)}
                                        >
                                            <MoreHorizSharpIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={10} sx={{
                                    textAlign: 'center', backgroundColor: 'white',
                                    height: '200px', fontWeight: 700
                                }}>
                                    No staff records for this hub yet
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal open={relieveOpen} onClose={() => setRelieveOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '92%', sm: 460 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>Mark for Relieving</Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 2 }}>
                        {relieveStaff?.full_name} · {relieveStaff?.employee_id}
                    </Typography>

                    <TextField
                        fullWidth required label="Resignation received on" type="date"
                        value={relieveForm.resignation_date}
                        onChange={(e) => {
                            const resignation = e.target.value;
                            setRelieveForm(prev => ({
                                ...prev,
                                resignation_date: resignation,
                                last_working_day: dayjs(resignation)
                                    .add(Number(prev.notice_period_days) || 0, 'day')
                                    .format('YYYY-MM-DD'),
                            }));
                        }}
                        InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth label="Notice period (days)" type="number"
                            value={relieveForm.notice_period_days}
                            onChange={(e) => {
                                const notice = e.target.value;
                                setRelieveForm(prev => ({
                                    ...prev,
                                    notice_period_days: notice,
                                    last_working_day: dayjs(prev.resignation_date)
                                        .add(Number(notice) || 0, 'day')
                                        .format('YYYY-MM-DD'),
                                }));
                            }}
                        />
                        <TextField
                            fullWidth required label="Last working day" type="date"
                            value={relieveForm.last_working_day}
                            onChange={(e) => setRelieveForm({ ...relieveForm, last_working_day: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>

                    <TextField
                        fullWidth multiline rows={2} label="Reason"
                        value={relieveForm.reason}
                        onChange={(e) => setRelieveForm({ ...relieveForm, reason: e.target.value })}
                        sx={{ mb: 2 }}
                    />

                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mb: 2 }}>
                        This sets their status to <b>On Notice</b> and opens a clearance checklist.
                        They stay on the attendance grid until the last working day, after which the
                        nightly job marks them relieved and disables any linked login.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setRelieveOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={submitRelieve} disabled={saving}>
                            Start relieving
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={balanceOpen} onClose={() => setBalanceOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '94%', sm: 540 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 3,
                    borderRadius: 2, maxHeight: '90vh', overflow: 'auto'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        <Box>
                            <Typography variant="h6">Leave Balance</Typography>
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                {balanceStaff?.full_name} · {balanceStaff?.employee_id}
                            </Typography>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: '110px' }}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={balanceYear} label="Year"
                                onChange={(e) => openBalances(balanceStaff, e.target.value)}
                            >
                                {[0, 1, 2].map(offset => {
                                    const year = new Date().getFullYear() - 1 + offset;
                                    return <MenuItem key={year} value={year}>{year}</MenuItem>;
                                })}
                            </Select>
                        </FormControl>
                    </Box>

                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mb: 2 }}>
                        Entitlements are per year. A year with no entitlement means every leave
                        request that year lands as loss of pay.
                    </Typography>

                    {balanceLoading ? (
                        [0, 1, 2].map(i => <Skeleton key={i} animation="wave" sx={{ height: '52px' }} />)
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {['Type', 'Entitled', 'Carried fwd', 'Used', 'Left'].map((h, i) => (
                                            <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', fontSize: '11px' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {balances.filter(b => b.leave_category !== 'lop').map(row => (
                                        <TableRow key={row.leave_category}>
                                            <TableCell sx={{ fontSize: '12px' }}>{row.label}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small" type="number" inputProps={{ step: '0.5', min: 0 }}
                                                    value={balanceValue(row, 'entitled')}
                                                    onChange={(e) => editBalance(row, 'entitled', e.target.value)}
                                                    sx={{ width: '86px', '& input': { fontSize: '12px', padding: '6px 8px' } }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small" type="number" inputProps={{ step: '0.5', min: 0 }}
                                                    value={balanceValue(row, 'carried_forward')}
                                                    onChange={(e) => editBalance(row, 'carried_forward', e.target.value)}
                                                    sx={{ width: '86px', '& input': { fontSize: '12px', padding: '6px 8px' } }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '12px', color: 'text.secondary' }}>{row.used}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>{row.available}</TableCell>
                                        </TableRow>
                                    ))}
                                    {balances.filter(b => b.leave_category === 'lop').map(row => (
                                        <TableRow key="lop">
                                            <TableCell sx={{ fontSize: '12px' }}>{row.label}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', color: 'text.secondary' }}>—</TableCell>
                                            <TableCell sx={{ fontSize: '12px', color: 'text.secondary' }}>—</TableCell>
                                            <TableCell sx={{ fontSize: '12px', color: 'borders.red' }}>{row.used}</TableCell>
                                            <TableCell sx={{ fontSize: '11px', color: 'text.secondary' }}>read-only</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setBalanceOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={saveBalances} disabled={saving || balanceLoading}>
                            Save
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={modalOpen} onClose={handleCloseModal}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '92%', sm: 520 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 4,
                    borderRadius: 2, maxHeight: '90vh', overflow: 'auto'
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {editMode ? 'Edit Staff' : 'Add Staff'}
                    </Typography>

                    {!editMode && (
                        <TextField
                            fullWidth
                            label="Employee ID"
                            value="Auto-generated on save"
                            disabled
                            sx={{ mb: 2 }}
                        />
                    )}

                    <TextField
                        fullWidth required label="Full name" value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth label="Mobile" value={formData.mobileno}
                            onChange={(e) => setFormData({ ...formData, mobileno: e.target.value })}
                        />
                        <TextField
                            fullWidth required label="Date of joining" type="date"
                            value={formData.date_of_joining}
                            onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Department</InputLabel>
                            <Select
                                value={formData.department_id} label="Department"
                                onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                            >
                                <MenuItem value="">None</MenuItem>
                                {departments.map(dep => (
                                    <MenuItem key={dep.id} value={dep.id}>{dep.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth label="Designation" placeholder="e.g. Picker"
                            value={formData.designation}
                            onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Hub</InputLabel>
                            <Select
                                value={formData.hub_id} label="Hub"
                                onChange={(e) => setFormData({ ...formData, hub_id: e.target.value })}
                            >
                                {(inventory || []).map(inv => (
                                    <MenuItem key={inv.value} value={inv.value}>{inv.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Shift</InputLabel>
                            <Select
                                value={formData.shift_id} label="Shift"
                                onChange={(e) => setFormData({ ...formData, shift_id: e.target.value })}
                            >
                                <MenuItem value="">None</MenuItem>
                                {shifts.map(shift => (
                                    <MenuItem key={shift.id} value={shift.id}>
                                        {shift.name} · {shift.timing_label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Reporting manager</InputLabel>
                        <Select
                            value={formData.reporting_manager_id} label="Reporting manager"
                            onChange={(e) => setFormData({ ...formData, reporting_manager_id: e.target.value })}
                        >
                            <MenuItem value="">None</MenuItem>
                            {managers.map(m => (
                                <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {editMode && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={formData.status} label="Status"
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            >
                                {STATUS_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Autocomplete
                        options={userOptions}
                        getOptionLabel={(option) => option.label || ''}
                        loading={userLoading}
                        onInputChange={(e, value) => setUserSearch(value)}
                        onChange={(e, value) => {
                            setFormData({ ...formData, user_id: value ? value.id : '' });
                            setLinkedUserLabel(value ? value.label : '');
                        }}
                        noOptionsText={userSearch.length < 3 ? 'Type at least 3 characters' : 'No unlinked accounts found'}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Link login account"
                                placeholder={linkedUserLabel || 'Search by mobile — optional'}
                                helperText="Leave empty for staff who don't log in. Can be linked later."
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {userLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                            {editMode ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
